import type { SupportCaseSeverity } from "./support-case";

const london = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/London",
  calendar: "gregory",
  numberingSystem: "latn",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

type LocalParts = Readonly<{ date: string; hour: number; minute: number; second: number }>;

function localParts(epoch: number): LocalParts {
  const parts = london.formatToParts(epoch);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  const year = value("year");
  const month = value("month");
  const day = value("day");
  return {
    date: `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
    hour: value("hour"),
    minute: value("minute"),
    second: value("second"),
  };
}

function zonedCalendarEpoch(epoch: number): number {
  const parts = localParts(epoch);
  return Date.parse(
    `${parts.date}T${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}:${String(parts.second).padStart(2, "0")}.000Z`,
  );
}

function resolveLondonInstant(date: string, hour: 9 | 18): number {
  const desired = Date.parse(`${date}T${String(hour).padStart(2, "0")}:00:00.000Z`);
  const offsets = new Set<number>();
  for (let hours = -48; hours <= 48; hours += 6) {
    const probe = desired + hours * 3_600_000;
    offsets.add(zonedCalendarEpoch(probe) - probe);
  }
  const candidates = [...offsets]
    .map((offset) => desired - offset)
    .filter((candidate) => zonedCalendarEpoch(candidate) === desired);
  if (candidates.length === 0) throw new RangeError("The London support clock is unavailable.");
  return Math.min(...candidates);
}

function addCalendarDays(date: string, days: number): string {
  const instant = new Date(`${date}T00:00:00.000Z`);
  instant.setUTCDate(instant.getUTCDate() + days);
  return instant.toISOString().slice(0, 10);
}

function isBusinessDate(date: string): boolean {
  const day = new Date(`${date}T12:00:00.000Z`).getUTCDay();
  return day >= 1 && day <= 5;
}

function nextBusinessOpening(date: string, includeDate: boolean): number {
  let candidate = includeDate ? date : addCalendarDays(date, 1);
  for (let attempt = 0; attempt < 8; attempt += 1) {
    if (isBusinessDate(candidate)) return resolveLondonInstant(candidate, 9);
    candidate = addCalendarDays(candidate, 1);
  }
  throw new RangeError("The London support clock is unavailable.");
}

const reviewHours: Readonly<Record<SupportCaseSeverity, number>> = {
  S1: 4,
  S2: 9,
  S3: 27,
  S4: 45,
};

function addSupportBusinessHours(receivedAtEpochMilliseconds: number, hours: number): number {
  if (!Number.isSafeInteger(receivedAtEpochMilliseconds) || receivedAtEpochMilliseconds < 0)
    throw new RangeError("The received time is unavailable.");
  let cursor = receivedAtEpochMilliseconds;
  let remaining = hours * 60 * 60_000;
  for (let segment = 0; segment < 16; segment += 1) {
    const local = localParts(cursor);
    const localMinute = local.hour * 60 + local.minute;
    if (!isBusinessDate(local.date) || localMinute >= 18 * 60) {
      cursor = nextBusinessOpening(local.date, false);
      continue;
    }
    if (localMinute < 9 * 60) cursor = nextBusinessOpening(local.date, true);
    const activeDate = localParts(cursor).date;
    const close = resolveLondonInstant(activeDate, 18);
    const available = close - cursor;
    if (remaining <= available) return cursor + remaining;
    remaining -= available;
    cursor = nextBusinessOpening(activeDate, false);
  }
  throw new RangeError("The support review target exceeds the supported clock range.");
}

/** Version 1: Monday-Friday 09:00-18:00 Europe/London; no holiday calendar. */
export function calculateSupportReviewTarget(
  receivedAtEpochMilliseconds: number,
  severity: SupportCaseSeverity,
): number {
  return addSupportBusinessHours(receivedAtEpochMilliseconds, reviewHours[severity]);
}

/** The final 20% of the severity-specific business-time budget. */
export function calculateSupportApproachingAt(
  receivedAtEpochMilliseconds: number,
  severity: SupportCaseSeverity,
): number {
  return addSupportBusinessHours(receivedAtEpochMilliseconds, reviewHours[severity] * 0.8);
}
